import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu'
import { Link } from 'react-router-dom'
import { encodePath, useActionable } from '../helpers'
import { usePageInfo } from '../config/queries'
import { useGuild } from '../config/context/guild'
import { hasAccessToContent } from './ProtectedContent'
import { AccessLevel, Section } from 'app/frontend/types/custom'

type MenuItem = {
  name: string | React.ReactNode
  path: string
  width: number
  exact?: boolean
  section?: Section
  requirement?: AccessLevel
  children?: Omit<MenuItem, 'children' | 'width'>[]
}

interface Props {
  menuItems: MenuItem[]
}

const Subheader = ({ menuItems }: Props) => {
  const {
    data: { user },
  } = usePageInfo()
  const { currentAction } = useActionable(user)
  const { teamReference, guild } = useGuild()

  const menuItemsWithAccess = menuItems.filter((item) => {
    return hasAccessToContent(
      item.requirement ?? 'none',
      item.section ?? 'base',
      guild,
      teamReference,
    ).hasAccess
  })

  const fitItemsWithinWidth = (items: typeof menuItems, maxWidth: number) => {
    let totalWidth = 0
    const visibleItems: typeof menuItems = []

    for (const item of items) {
      if (totalWidth + item.width > maxWidth) break
      visibleItems.push(item)
      totalWidth += item.width
    }

    return visibleItems
  }

  const availableWidth =
    window.innerWidth >= 640 ? window.innerWidth - 140 : window.innerWidth - 90
  const visibleMenuItems = fitItemsWithinWidth(
    menuItemsWithAccess,
    availableWidth,
  )

  const invisibleMenuItems = menuItemsWithAccess.slice(visibleMenuItems.length)
  if (invisibleMenuItems.length === 1 && visibleMenuItems.length > 0) {
    invisibleMenuItems.push(visibleMenuItems.pop() as typeof menuItems[number])
  }

  return (
    <div
      className={`border-b-2 -ml-8 -mr-8 max-sm:-ml-4 max-sm:-mr-4 sticky min-h-[40px] ${
        currentAction ? 'top-[88px]' : 'top-[57px]'
      } bg-background z-40`}
    >
      <div className='group flex flex-1 list-none items-center max-sm:justify-center sm:mx-8 space-x-0 max-sm:w-screen max-sm:[&>li]:grow max-sm:flex max-sm:[&_a]:w-full'>
        {visibleMenuItems.map((item) => {
          const encodedPath = encodePath(item.path)
          const childrenWithAccess =
            item.children?.filter((child) => {
              return hasAccessToContent(
                child.requirement ?? item.requirement ?? 'none',
                child.section ?? item.section ?? 'base',
                guild,
                teamReference,
              ).hasAccess
            }) ?? []

          const target =
            childrenWithAccess.length === 1 ? childrenWithAccess[0] : item
          const encodedTargetPath = encodePath(target.path)

          return (
            <div key={item.path}>
              {childrenWithAccess.length > 1 ? (
                <NavigationMenu delayDuration={50}>
                  <NavigationMenuList>
                    <NavigationMenuItem>
                      <NavigationMenuTrigger
                        onClick={(e) => e.preventDefault()}
                        className={`cursor-default mb-[-2px] pr-2 bg-background border-b-2 ${
                          (
                            item.exact
                              ? window.location.pathname === encodedPath
                              : window.location.pathname.includes(encodedPath)
                          )
                            ? 'border-b-foreground bg-card'
                            : ''
                        } rounded-none`}
                      >
                        {item.name}
                      </NavigationMenuTrigger>
                      <NavigationMenuContent>
                        <div>
                          {childrenWithAccess.map((child) => {
                            const encodedChildPath = encodePath(child.path)
                            return (
                              <NavigationMenuLink
                                key={child.path}
                                active={window.location.pathname.includes(
                                  encodedChildPath,
                                )}
                                className={`${navigationMenuTriggerStyle()} hover:bg-card rounded-none last:rounded-b-md min-w-full data-active:text-primary`}
                                asChild
                              >
                                <Link to={child.path}>{child.name}</Link>
                              </NavigationMenuLink>
                            )
                          })}
                        </div>
                      </NavigationMenuContent>
                    </NavigationMenuItem>
                  </NavigationMenuList>
                </NavigationMenu>
              ) : (
                <Link
                  to={target.path}
                  data-active={
                    (target.exact
                      ? window.location.pathname === encodedTargetPath
                      : window.location.pathname.includes(encodedTargetPath)) ||
                    undefined
                  }
                  className={`${navigationMenuTriggerStyle()} mb-[-2px] bg-transparent border-b-2 border-b-transparent hover:border-b-border data-active:border-b-foreground rounded-none`}
                >
                  {target.name}
                </Link>
              )}
            </div>
          )
        })}

        {invisibleMenuItems.length > 0 ? (
          <NavigationMenu delayDuration={50}>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger
                  className={`${navigationMenuTriggerStyle()} mb-[-2px] bg-background border-b-2 rounded-none`}
                >
                  More
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <div className='flex flex-col'>
                    {invisibleMenuItems.map((item) => {
                      const encodedPath = encodePath(item.path)
                      return (
                        <NavigationMenuLink
                          key={item.path}
                          active={
                            item.exact
                              ? window.location.pathname === encodedPath
                              : window.location.pathname.includes(encodedPath)
                          }
                          className={`${navigationMenuTriggerStyle()} hover:bg-card rounded-none last:rounded-b-md min-w-full`}
                          asChild
                        >
                          <Link to={item.path}>{item.name}</Link>
                        </NavigationMenuLink>
                      )
                    })}
                  </div>
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        ) : null}
      </div>
    </div>
  )
}

export default Subheader
